import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import {
  useUpdateCofStatusMutation,
  useUpdateCorporateCOFMutation,
} from '../../../corporate/store/services/corporate';
import { useLazyGetCustomersEmployeeQuery } from '../../../../store/dictionaries/services';
import { NatureOfControlEnum } from '../../../new-application/types';
import {
  Company,
  CofStatus,
  SignatureType,
  AccessRight,
} from '../../../common/types';
import { CofProps } from '../../../corporate/types';

const useCompanyItem = ({
  value,
  list,
  removed,
}: {
  value: Company;
  list?: Company[];
  removed?: boolean;
}) => {
  const [formValues, setFormValues] = useState<Company>(value);
  const [defaultFormValues, setDefaultFormValues] = useState<
    Company | undefined
  >(undefined);
  const [rejectReason, setRejectReason] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [popupIsVisible, setPopupIsVisible] = useState(false);

  const [updateCof, { isLoading: cofIsUpdate }] =
    useUpdateCorporateCOFMutation();
  const [
    updateCofStatus,
    { isLoading: updateStatusInProgress, isSuccess: updateStatusSuccess },
  ] = useUpdateCofStatusMutation();
  const [approveCof, { isLoading: approveInProgress }] =
    useUpdateCofStatusMutation();
  const [getCustomersEmployee] = useLazyGetCustomersEmployeeQuery();

  const getCustomersEmployeeAction = useCallback(async (id: string) => {
    const response = await getCustomersEmployee(id);

    if (response.data?.data) {
      setEmployeeName(
        `${response.data.data?.profile?.firstName} ${response.data.data?.profile?.lastName}`,
      );
    }
  }, []);

  const updateCofAction = useCallback(async () => {
    const body: {
      position: string;
      percentage?: string;
      natureOfControl?: NatureOfControlEnum;
      companyOfficialType: string;
      signatureType: SignatureType;
      accessRight: string;
    } = {
      position: formValues.position,
      companyOfficialType: value?.companyOfficialType,
      signatureType: formValues.signatureType,
      accessRight: formValues.accessRight as string,
    };

    if (formValues.havingControl) {
      body.percentage = formValues.percentage;
      body.natureOfControl = formValues.natureOfControl;
    } else if (!formValues.havingControl && value?.percentage) {
      body.percentage = '';
      body.natureOfControl = undefined;
    }

    const response = (await updateCof({
      id: value?.id,
      body,
    })) as { data: { data: CofProps } };

    if (response?.data?.data) {
      setDefaultFormValues({
        ...formValues,
        havingControl: Boolean(response.data.data.percentage),
      });
    }

    return response;
  }, [formValues]);

  const onUpdateCofStatus = (status: CofStatus) => {
    const body: {
      status: CofStatus;
      rejectionReason?: string;
    } = {
      status,
    };

    if (rejectReason) {
      body.rejectionReason = rejectReason;
    }

    updateCofStatus({
      id: value?.id,
      body,
    });
  };

  const onApprove = async () => {
    let approveIsNotAllowed = false;

    if (!isEqual(formValues, defaultFormValues)) {
      approveIsNotAllowed = true;

      const updateCofResponse = await updateCofAction();

      if (updateCofResponse && updateCofResponse.data) {
        approveIsNotAllowed = false;
      }
    }

    if (!approveIsNotAllowed) {
      const response = (await approveCof({
        id: value?.id,
        body: {
          status: CofStatus.APPROVED,
        },
      })) as { data: { data: CofProps } };

      if (response?.data?.data) {
        if (value?.companyOfficialType === 'MAIN_REPRESENTATIVE') {
          if (
            list
              ?.filter((item) => item.id !== value.id)
              .find(
                (item) => item.companyOfficialType === 'MAIN_REPRESENTATIVE',
              )
          ) {
            const prevMainRepresentative = list
              ?.filter(
                (item) =>
                  item.id !== value.id && item.status === CofStatus.APPROVED,
              )
              .find(
                (item) => item.companyOfficialType === 'MAIN_REPRESENTATIVE',
              );

            if (prevMainRepresentative) {
              updateCofStatus({
                id: prevMainRepresentative?.id,
                body: {
                  status: CofStatus.ARCHIVED,
                  rejectionReason:
                    'Another user approved as Main Representative',
                },
              });
            }
          }
        }

        setDefaultFormValues({
          ...formValues,
          havingControl: Boolean(response.data.data.percentage),
        });
      }
    }
  };

  useEffect(() => {
    if (value) {
      const nextFormValues = { ...value };

      nextFormValues.havingControl = Boolean(value.percentage);
      nextFormValues.accessRight = value.accessRight || AccessRight.NONE;

      setFormValues(nextFormValues);

      if (!defaultFormValues) {
        setDefaultFormValues(nextFormValues);
      }
    }
  }, [value, defaultFormValues]);

  useEffect(() => {
    if (value) {
      if (removed) {
        getCustomersEmployeeAction(value.archivedBy || value.rejectedBy);
      } else if (!removed && value.approvedBy) {
        getCustomersEmployeeAction(value.approvedBy);
      }
    }
  }, [value, removed]);

  useEffect(() => {
    if (updateStatusSuccess) {
      setPopupIsVisible(false);
      setRejectReason('');
    }
  }, [updateStatusSuccess]);

  return {
    formValues,
    updateStatusInProgress,
    employeeName,
    approveInProgress,
    defaultFormValues,
    cofIsUpdate,
    onApprove,
    setFormValues,
    updateCofAction,
    setPopupIsVisible,
    setRejectReason,
    onUpdateCofStatus,
    popupIsVisible,
    rejectReason,
  };
};

export default useCompanyItem;
